import React from "react";
import { ArrowRight, DollarSign } from "lucide-react";
import PricingTier from "../PricingTier/PricingTier";
import SectionHeading from "../SectionHeading/SectionHeading";

const TIERS = [
  {
    label: "All CertainPath Members",
    type: "Standard Rate",
    price: "$175",
    per: "per license / per month",
    isFeatured: false,
  },
  {
    label: "CertainPath Training Members",
    type: "Training Member Rate",
    price: "$150",
    per: "per license / per month",
    badge: "Best Value",
    save: "Save $25/month per license",
    isFeatured: true,
  },
];

const INCLUDED = [
  "Unlimited call recordings",
  "Voice ID & speaker separation",
  "AI coaching insights",
  "Full call transcripts",
  "Script & process scoring",
  "Sentiment & tone analysis",
  "Team performance dashboards",
  "At-a-Glance reporting",
  "Dedicated support",
];

interface PricingBlockProps {
  /** Anchor the CTAs scroll to. */
  formSectionId: string;
}

const PricingBlock: React.FC<PricingBlockProps> = ({ formSectionId }) => (
  <section className="relative bg-mist pb-24 dark:bg-slate-950" id="pricing">
    <div className="mx-auto max-w-7xl px-6">
      <div className="mx-auto mb-14 max-w-4xl text-center">
        <a
          className="group mx-auto mb-16 inline-flex w-fit items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-px hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary"
          href={`#${formSectionId}`}
        >
          Get Started with FieldCoach
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>

        <div className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-gold/45 bg-cream-dark px-4 py-2 text-sm font-bold text-primary dark:bg-primary/15 dark:text-primary-light">
          <DollarSign className="h-4 w-4" />
          Transparent, Member-First Pricing
        </div>

        <SectionHeading>
          Built Around Your Team&mdash;Not Guesswork.
        </SectionHeading>
        <p className="mt-5 text-lg leading-relaxed text-gray-500 dark:text-gray-400">
          One annual commitment. Every feature. Every rep. Priced per license so
          you only pay for the seats you need&mdash;with the best rate reserved
          for CertainPath Training members.
        </p>
        <p className="mt-3.5 text-base text-gray-500 dark:text-gray-400">
          Predictable monthly billing. No tier juggling. No surprises.
        </p>
      </div>

      <div className="relative mx-auto max-w-5xl rounded-3xl border border-gray-200 bg-white px-6 py-12 shadow-lg dark:border-slate-700 dark:bg-slate-800 md:px-10">
        <div className="mb-10 text-center">
          <div className="mb-4 inline-block rounded-full border border-gold/45 bg-cream-dark px-4 py-1.5 text-xs font-bold text-primary dark:bg-primary/15 dark:text-primary-light">
            Annual Contract &middot; All Features Included
          </div>
          <h3 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white md:text-4xl">
            1-Year FieldCoach Contract
          </h3>
          <div className="mt-2.5 text-base text-gray-500 dark:text-gray-400">
            Per license &middot; Billed monthly
          </div>
        </div>

        <div className="mx-auto grid max-w-3xl grid-cols-1 gap-5 md:grid-cols-2">
          {TIERS.map((tier) => (
            <PricingTier
              badge={tier.badge}
              isFeatured={tier.isFeatured}
              key={tier.label}
              label={tier.label}
              per={tier.per}
              price={tier.price}
              save={tier.save}
              type={tier.type}
            />
          ))}
        </div>

        <div className="mt-11 border-t border-gray-200 pt-8 text-center dark:border-slate-700">
          <div className="mb-5 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            Every license includes
          </div>
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-x-8 gap-y-3.5 md:grid-cols-3">
            {INCLUDED.map((item) => (
              <div
                className="relative pl-6 text-left text-sm font-medium text-gray-900 dark:text-white"
                key={item}
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 font-extrabold text-primary dark:text-primary-light"
                >
                  ✓
                </span>
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          12-month minimum commitment. Volume discounts available for larger
          teams &mdash;{" "}
          <a
            className="font-bold text-primary no-underline hover:underline dark:text-primary-light"
            href={`#${formSectionId}`}
          >
            contact us
          </a>
          .
        </div>
      </div>
    </div>
  </section>
);

export default PricingBlock;
